import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import Layout from '../../components/Layout';
import DataTable, { Column, DataTableFetchParams, DataTableFilterDef } from '../../components/ui/DataTable';
import { bookingsService } from '../../services/bookings.service';
import { BookingAdmin, BookingStatus, BookingPaymentStatus } from '../../interfaces/booking.interface';
import BookingDetailDialog from './BookingDetailDialog';

const BOOKING_STATUS_BADGE: Record<BookingStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  no_show: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const PAYMENT_STATUS_BADGE: Record<BookingPaymentStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  succeeded: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const formatAmount = (amount: number, currency: string) =>
  `${currency} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BookingsPage = () => {
  const { t } = useTranslation('common');
  const [viewId, setViewId] = useState<string | null>(null);
  const closeView = useCallback(() => setViewId(null), []);

  const COLUMNS: Column<BookingAdmin>[] = [
    {
      key: 'confirmationCode',
      header: t('bookings_confirmation'),
      render: (b) => (
        <button
          type="button"
          onClick={() => setViewId(b.id)}
          className="font-mono text-xs whitespace-nowrap text-blue-600 hover:underline dark:text-blue-400"
        >
          {b.confirmationCode ?? '—'}
        </button>
      ),
    },
    {
      key: 'buyer',
      header: t('bookings_buyer'),
      render: (b) => b.buyer ? (
        <div className="max-w-[160px]" title={`${b.buyer.fullName ?? ''} ${b.buyer.email}`.trim()}>
          <div className="font-medium text-sm truncate">{b.buyer.fullName ?? '—'}</div>
          <div className="text-xs text-gray-500 truncate">{b.buyer.email}</div>
        </div>
      ) : <span className="text-gray-400">—</span>,
    },
    {
      key: 'seller',
      header: t('bookings_seller_company'),
      render: (b) => (
        <div>
          <div className="font-medium text-sm">{b.seller?.fullName ?? '—'}</div>
          {b.company && <div className="text-xs text-gray-500">{b.company.name}</div>}
        </div>
      ),
    },
    {
      key: 'listing',
      header: t('bookings_listing'),
      render: (b) => (
        <div className="text-sm max-w-[180px] line-clamp-2" title={b.listing?.title ?? undefined}>
          {b.listing?.title ?? '—'}
        </div>
      ),
    },
    {
      key: 'dates',
      header: t('bookings_dates'),
      render: (b) => (
        <span className="text-sm whitespace-nowrap">
          {format(new Date(b.fromDate), 'dd MMM yyyy')} → {format(new Date(b.toDate), 'dd MMM yyyy')}
        </span>
      ),
    },
    {
      key: 'depositAmount',
      header: t('bookings_deposit'),
      render: (b) => (
        <span className="text-sm font-mono whitespace-nowrap">
          {formatAmount(b.depositAmount, b.currency)}
        </span>
      ),
    },
    {
      key: 'extraAmount',
      header: t('bookings_extras'),
      render: (b) => (
        <span className="text-sm font-mono whitespace-nowrap">
          {formatAmount(b.extrasTotal + b.extraFeeAmount, b.currency)}
        </span>
      ),
    },
    {
      key: 'totalAmount',
      header: t('bookings_total'),
      render: (b) => (
        <span className="text-sm font-mono font-semibold whitespace-nowrap">
          {formatAmount(b.totalAmount, b.currency)}
        </span>
      ),
    },
    {
      key: 'paymentStatus',
      header: t('bookings_payment'),
      render: (b) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PAYMENT_STATUS_BADGE[b.paymentStatus]}`}>
          {b.paymentStatus}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('status'),
      render: (b) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${BOOKING_STATUS_BADGE[b.status]}`}>
          {b.status.replace('_', ' ')}
        </span>
      ),
    },
  ];

  const FILTER_DEFS: DataTableFilterDef[] = [
    {
      key: 'status',
      label: t('bookings_booking_status'),
      type: 'select',
      options: [
        { label: 'All Statuses', value: '' },
        { label: 'Pending', value: 'pending' },
        { label: 'Completed', value: 'completed' },
        { label: 'No Show', value: 'no_show' },
        { label: 'Cancelled', value: 'cancelled' },
      ],
    },
    {
      key: 'paymentStatus',
      label: t('bookings_payment_status'),
      type: 'select',
      options: [
        { label: 'All', value: '' },
        { label: 'Pending', value: 'pending' },
        { label: 'Succeeded', value: 'succeeded' },
        { label: 'Failed', value: 'failed' },
      ],
    },
  ];

  const fetchBookings = useCallback(async (params: DataTableFetchParams) => {
    const result = await bookingsService.getBookings({
      page: params.page,
      take: params.pageSize,
      status: params.filters?.status || undefined,
      paymentStatus: params.filters?.paymentStatus || undefined,
      search: params.search || undefined,
    });
    return { data: result.data, total: result.meta.itemCount, totalPages: result.meta.pageCount };
  }, []);

  return (
    <Layout title={t('bookings')}>
      <DataTable<BookingAdmin>
        columns={COLUMNS}
        onFetch={fetchBookings}
        filterDefs={FILTER_DEFS}
        enableSearch
        enablePagination
        enableRefresh
      />
      <BookingDetailDialog bookingId={viewId} onClose={closeView} />
    </Layout>
  );
};

export default BookingsPage;
